import React, { useState } from 'react';
import {
  IonContent,
  IonPage,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonBackButton,
  IonTitle,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonButton,
  IonTextarea,
  IonInput,
  IonItem,
  IonLabel,
  IonText,
  IonList,
  IonCheckbox,
  useIonToast,
  useIonAlert,
} from '@ionic/react';
import { Link } from 'react-router-dom';
import { useAuth, useClerk, useUser } from '@clerk/clerk-react';
import { usePageView } from '../hooks/useLoggerInit';
import { useI18n } from '../i18n/I18nProvider';
import { apiUrl, apiHeaders } from '../lib/api';
import { clearLocalPersonalData } from '../lib/clearLocalPersonalData';

const IMPORT_UI_ENABLED =
  import.meta.env.VITE_DATA_IMPORT_ENABLED === '1' ||
  import.meta.env.VITE_DATA_IMPORT_ENABLED === 'true';

const PrivacyDataPage: React.FC = () => {
  usePageView('privacy_data');
  const { t } = useI18n();
  const { getToken } = useAuth();
  const { signOut } = useClerk();
  const { user } = useUser();
  const [presentToast] = useIonToast();
  const [presentAlert] = useIonAlert();

  const [importJson, setImportJson] = useState('');
  const [exportBusy, setExportBusy] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [confirmUnderstand, setConfirmUnderstand] = useState(false);

  const showToast = (message: string, color: string) => {
    void presentToast({ message, duration: 3500, color, position: 'bottom' });
  };

  const downloadExport = async (format: 'json' | 'zip') => {
    setExportBusy(true);
    try {
      const token = await getToken();
      const res = await fetch(
        apiUrl(`/api/me/data-export?format=${format}`),
        { headers: apiHeaders(token) }
      );
      if (res.status === 429) {
        showToast(t('privacyData.export.rateLimit'), 'warning');
        return;
      }
      if (!res.ok) {
        const j = (await res.json().catch(() => null)) as { message?: string } | null;
        showToast(j?.message ?? t('privacyData.export.error'), 'danger');
        return;
      }
      const blob = await res.blob();
      const dispo = res.headers.get('Content-Disposition');
      const nameMatch = dispo?.match(/filename="([^"]+)"/);
      const filename =
        nameMatch?.[1] ?? `export.${format === 'zip' ? 'zip' : 'json'}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      showToast(t('privacyData.export.success'), 'success');
    } catch {
      showToast(t('privacyData.export.error'), 'danger');
    } finally {
      setExportBusy(false);
    }
  };

  const runImport = async () => {
    setImportBusy(true);
    try {
      let body: unknown;
      try {
        body = JSON.parse(importJson) as unknown;
      } catch {
        showToast(t('privacyData.import.invalidJson'), 'danger');
        return;
      }
      const token = await getToken();
      const res = await fetch(apiUrl('/api/me/data-import'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...apiHeaders(token),
        },
        body: JSON.stringify(body),
      });
      if (res.status === 404) {
        showToast(t('privacyData.import.disabled'), 'medium');
        return;
      }
      if (!res.ok) {
        const j = (await res.json().catch(() => null)) as { message?: string } | null;
        showToast(j?.message ?? t('privacyData.import.error'), 'danger');
        return;
      }
      const data = (await res.json()) as { contactsCreated: number; keysImported: number };
      showToast(
        t('privacyData.import.success', {
          contacts: data.contactsCreated,
          keys: data.keysImported,
        }),
        'success'
      );
      setImportJson('');
    } catch {
      showToast(t('privacyData.import.error'), 'danger');
    } finally {
      setImportBusy(false);
    }
  };

  const runDelete = async () => {
    setDeleteBusy(true);
    try {
      const token = await getToken();
      const res = await fetch(apiUrl('/api/me/forget-me'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...apiHeaders(token),
        },
        body: JSON.stringify({
          confirmEmail,
          confirmUnderstand: true,
        }),
      });
      if (res.status === 502) {
        clearLocalPersonalData();
        showToast(t('privacyData.delete.clerkError'), 'warning');
        await signOut();
        return;
      }
      if (!res.ok) {
        const j = (await res.json().catch(() => null)) as { message?: string } | null;
        showToast(j?.message ?? t('privacyData.forgetMe.error'), 'danger');
        return;
      }
      clearLocalPersonalData();
      showToast(t('privacyData.forgetMe.success'), 'success');
      await signOut();
    } catch {
      showToast(t('privacyData.forgetMe.error'), 'danger');
    } finally {
      setDeleteBusy(false);
    }
  };

  const confirmForgetMe = () => {
    void presentAlert({
      header: t('privacyData.forgetMe.alertTitle'),
      message: t('privacyData.forgetMe.alertMessage'),
      buttons: [
        { text: t('common.actions.cancel'), role: 'cancel' },
        {
          text: t('privacyData.forgetMe.alertConfirm'),
          role: 'destructive',
          handler: () => {
            void runDelete();
          },
        },
      ],
    });
  };

  const accountEmail = (user?.primaryEmailAddress?.emailAddress ?? '')
    .trim()
    .toLowerCase();
  const emailMatches =
    confirmEmail.trim().toLowerCase() === accountEmail && accountEmail.length > 0;
  const canForgetMe = confirmUnderstand && emailMatches && !deleteBusy;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton
              defaultHref="/dashboard"
              text={t('common.actions.back')}
            />
          </IonButtons>
          <IonTitle>{t('privacyData.title')}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen className="ion-padding">
        <p className="intro">{t('privacyData.intro')}</p>
        <p className="legal-link">
          <Link to="/privacy">{t('privacyData.linkPrivacy')}</Link>
        </p>

        <IonCard>
          <IonCardHeader>
            <IonCardTitle>{t('privacyData.export.title')}</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonText>
              <p>{t('privacyData.export.body')}</p>
            </IonText>
            <div className="btn-row">
              <IonButton
                expand="block"
                disabled={exportBusy}
                onClick={() => void downloadExport('json')}
              >
                {exportBusy ? t('privacyData.export.busy') : t('privacyData.export.button')}
              </IonButton>
              <IonButton
                expand="block"
                fill="outline"
                disabled={exportBusy}
                onClick={() => void downloadExport('zip')}
              >
                {exportBusy ? t('privacyData.export.busy') : t('privacyData.export.buttonZip')}
              </IonButton>
            </div>
          </IonCardContent>
        </IonCard>

        {IMPORT_UI_ENABLED && (
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>{t('privacyData.import.title')}</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonText>
                <p>{t('privacyData.import.body')}</p>
              </IonText>
              <IonTextarea
                className="import-json"
                rows={8}
                placeholder={t('privacyData.import.placeholder')}
                value={importJson}
                onIonInput={(e) => setImportJson(e.detail.value ?? '')}
              />
              <IonButton
                expand="block"
                disabled={importBusy || !importJson.trim()}
                onClick={() => void runImport()}
              >
                {importBusy ? t('common.loading') : t('privacyData.import.button')}
              </IonButton>
            </IonCardContent>
          </IonCard>
        )}

        <IonCard className="privacy-delete-card">
          <IonCardHeader>
            <IonCardTitle className="privacy-delete-card__title">
              {t('privacyData.forgetMe.title')}
            </IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonText className="privacy-delete-card__body">
              <p>{t('privacyData.forgetMe.subtitle')}</p>
              <p>{t('privacyData.forgetMe.body')}</p>
              <p className="forget-me-tip">{t('privacyData.forgetMe.exportTip')}</p>
              <ul className="forget-me-list">
                {(t('privacyData.forgetMe.dataList') as string).split('|').map((item) => (
                  <li key={item}>{item.trim()}</li>
                ))}
              </ul>
              <p className="forget-me-processors">{t('privacyData.forgetMe.processors')}</p>
            </IonText>
            <IonList lines="none" className="privacy-delete-card__list">
              <IonItem className="privacy-delete-card__item">
                <IonCheckbox
                  slot="start"
                  checked={confirmUnderstand}
                  onIonChange={(e) => setConfirmUnderstand(e.detail.checked)}
                />
                <IonLabel className="ion-text-wrap">
                  {t('privacyData.forgetMe.confirmUnderstand')}
                </IonLabel>
              </IonItem>
              <IonItem className="privacy-delete-card__item">
                <IonLabel position="stacked" className="privacy-delete-card__label">
                  {t('privacyData.forgetMe.confirmEmailLabel')}
                </IonLabel>
                <IonInput
                  className="privacy-delete-card__input"
                  type="email"
                  autocomplete="email"
                  placeholder={t('privacyData.forgetMe.confirmEmailPlaceholder')}
                  value={confirmEmail}
                  onIonInput={(e) => setConfirmEmail(e.detail.value ?? '')}
                />
              </IonItem>
            </IonList>
            <p className="hint-email privacy-delete-card__hint">
              {user?.primaryEmailAddress?.emailAddress ?? ''}
            </p>
            <IonButton
              expand="block"
              color="danger"
              disabled={!canForgetMe}
              onClick={() => confirmForgetMe()}
            >
              {deleteBusy ? t('privacyData.forgetMe.busy') : t('privacyData.forgetMe.button')}
            </IonButton>
          </IonCardContent>
        </IonCard>

        <style>{`
          .intro {
            line-height: 1.5;
            margin-bottom: 8px;
          }
          .legal-link {
            margin-bottom: 16px;
          }
          .legal-link a {
            color: var(--ion-color-primary);
            text-decoration: none;
          }
          .btn-row {
            display: flex;
            flex-direction: column;
            gap: 10px;
            margin-top: 16px;
          }
          .import-json {
            border: 1px solid var(--ion-color-medium-tint);
            border-radius: 8px;
            padding: 8px;
            margin: 12px 0;
          }
          .hint-email {
            font-size: 13px;
            margin: 8px 0 12px;
          }

          /* Delete section: avoid full IonCard danger tint (poor text contrast on tinted fill). */
          .privacy-delete-card {
            border: 1px solid var(--ion-color-danger);
            border-left-width: 4px;
            background: var(--ion-background-color, #fff);
            --color: var(--ion-text-color);
          }
          .privacy-delete-card__title {
            /* Main danger token reads on light and dark backgrounds better than -shade/-tint alone. */
            color: var(--ion-color-danger);
            font-weight: 700;
          }
          .privacy-delete-card__body {
            color: var(--ion-text-color);
          }
          .privacy-delete-card__body p {
            color: inherit;
            line-height: 1.55;
            margin: 0 0 12px;
          }
          .privacy-delete-card__list {
            background: transparent;
            padding: 0;
          }
          .privacy-delete-card__item {
            --background: transparent;
            --border-color: transparent;
            --color: var(--ion-text-color);
            --padding-start: 0;
            --inner-padding-end: 0;
          }
          .privacy-delete-card__label {
            color: var(--ion-text-color);
            opacity: 0.95;
          }
          .privacy-delete-card__input {
            --color: var(--ion-text-color);
            --placeholder-color: var(--ion-color-medium-shade);
            --padding-start: 0;
          }
          .privacy-delete-card__hint {
            color: var(--ion-color-medium-shade);
            margin: 8px 0 12px;
          }
          .forget-me-tip {
            font-size: 14px;
            color: var(--ion-color-medium-shade);
            margin: 0 0 12px;
          }
          .forget-me-list {
            margin: 0 0 12px;
            padding-left: 20px;
            font-size: 14px;
            line-height: 1.5;
          }
          .forget-me-processors {
            font-size: 13px;
            color: var(--ion-color-medium-shade);
            margin: 0 0 8px;
          }
        `}</style>
      </IonContent>
    </IonPage>
  );
};

export default PrivacyDataPage;
